import { gql, useLazyQuery } from '@apollo/client'
import { Box } from '@mui/material'
import Button from '@mui/material/Button'
import {
  Campaign,
  CampaignChannel,
  Retailer,
} from '@promoboxx/graphql-gateway-types'
import useFilter from '@promoboxx/use-filter'
import I18n from 'i18n-js'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'

import RequestCampaignsDialog from '@src/components/content/RequestCampaignsDialog'
import urlParamStore from '@src/lib/urlParamStore'
import useOnRetailerAccountChange from '@src/lib/useOnRetailerAccountChange'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'

import cloudinary from '../../lib/Cloudinary'
import useModalState from '../../lib/useModalState'
import { currentRetailerAccountSelector } from '../../stores/app/selectors'
import { currentRetailerIdSelector } from '../../stores/retailers/selectors'

import CampaignResultsContainer from './CampaignResultsContainer'
import ContentFilters from './ContentFilters'

type NormalizedChannels = {
  slug: string
  type: CampaignChannel['channel']
  to: string
  count: number
}

const normalizeChannelType = (channelName: string) => {
  switch (channelName) {
    case 'asset':
      return 'downloadables'
    case 'embed':
      return 'banner'
    default:
      return channelName
  }
}

export const GET_CAMPAIGNS_SEARCH = gql`
  query GetCampaignsSearchContentPage(
    $resourceType: CampaignSearchResourceType!
    $resourceId: ID!
    $params: CampaignSearchParams
  ) {
    getCampaignSearch(
      resourceType: $resourceType
      resourceId: $resourceId
      params: $params
    ) {
      data {
        id
        name
        start_date
        end_date
        brand {
          id
          logo_url
          slug
        }
        creative_path
        description
        logo_public_id
        invitation {
          id
          state
          created_at
          status
        }
        socials {
          channel
          count
        }
        localAdBalances {
          id
          local_ads_balance
        }
      }
      paging {
        total_results
      }
    }
  }
`

const OrganicCampaigns: React.FC<
  React.PropsWithChildren<{
    brandFilter?: string
    filterBarRef: HTMLDivElement | null
  }>
> = ({ filterBarRef, brandFilter }) => {
  const pageSize = 24
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const [campaignCards, setCampaignCards] = useState<Campaign[]>([])
  const retailerId = useSelector(currentRetailerIdSelector)
  const { isOpen, toggleModal } = useModalState()
  const [getCampaignSearch, { data, refetch, called }] =
    useLazyQuery(GET_CAMPAIGNS_SEARCH)

  const defaultRetailerIds = retailerAccount.retailers?.map(
    (retailer: Retailer) => {
      if (brandFilter === 'brand') {
        return !retailer.brand?.community ? retailer.id : ''
      } else if (brandFilter === 'industry') {
        return retailer.brand?.community ? retailer.id : ''
      }
      return retailer.id
    },
  )

  const { previouslySelectedRetailers, setPreviouslySelectedRetailer } =
    usePreviouslySelectedBrand(undefined, brandFilter)

  const { filterInfo, updateFilter, setPage, resetFilter, isLoading } =
    useFilter('content.organic_campaigns', {
      shouldForceRunOnMount: true,
      defaultFilterInfo: {
        filter: {
          brands: previouslySelectedRetailers.map((x) => x.id),
          filterByChannel: [],
          showValue: [],
          tagsValues: [],
          searchValue: null,
        },
        pageSize,
      },
      onChange(filterInfo) {
        let params = {
          per_page:
            !called && filterInfo.offset
              ? filterInfo.offset + pageSize
              : pageSize,
          offset: !called && filterInfo.offset ? 0 : filterInfo.offset,
          retailer_ids: defaultRetailerIds,
          tags: [],
          search: null,
          filterParams: {
            show_state: undefined,
          },
        }

        if (filterInfo.filter.brands && filterInfo.filter.brands.length) {
          params.retailer_ids = filterInfo.filter.brands
        }
        if (
          filterInfo.filter.tagsValues &&
          filterInfo.filter.tagsValues.length
        ) {
          params.tags = filterInfo.filter.tagsValues
        }
        if (filterInfo.filter.searchValue) {
          params.search = filterInfo.filter.searchValue
        }
        if (
          filterInfo.filter.filterByChannel?.length &&
          filterInfo.filter.filterByChannel[0]
        ) {
          params.filterParams = filterInfo.filter.filterByChannel.reduce(
            (acc, f: string) => Object.assign(acc, { [f]: true }),
            { ...params.filterParams },
          )
        }
        if (
          filterInfo.filter.showValue?.length &&
          filterInfo.filter.showValue[0] !== '0'
        ) {
          params.filterParams.show_state = filterInfo.filter.showValue[0]
        }

        const variables = {
          resourceId:
            retailerId && !params.retailer_ids
              ? retailerId
              : retailerAccount.id,
          resourceType:
            retailerId && !params.retailer_ids
              ? 'retailers'
              : 'retailer_accounts',
          params,
        }

        return (called ? refetch(variables) : getCampaignSearch({ variables }))
          .then((response) => {
            return {
              filterInfo: {
                totalResults:
                  response?.data?.getCampaignSearch?.paging?.total_results || 0,
              },
            }
          })
          .catch((error) => {
            console.error(error)
          })
      },
      store: urlParamStore,
    })

  useEffect(() => {
    setPreviouslySelectedRetailer(filterInfo.filter.brands)
  }, [filterInfo.filter.brands, setPreviouslySelectedRetailer])

  useEffect(() => {
    if (!isLoading && data?.getCampaignSearch?.data) {
      // Normalize the data we get back into results
      const results =
        data?.getCampaignSearch?.data?.map((campaign: Campaign) => {
          // this might need additional params to be passed on back navigation for "back to content" r1 button
          const activateUrl = `/#/${retailerAccount.slug}/${campaign?.brand?.slug}/campaigns/${campaign?.creative_path}/activate`
          const channels: Array<NormalizedChannels> = []
          campaign?.socials?.forEach((social) => {
            const channel = normalizeChannelType(social?.channel || '')
            if (!social?.channel) return
            channels.push({
              slug: campaign?.brand?.slug || '',
              type: normalizeChannelType(social?.channel) || '',
              to: `${activateUrl}/${channel}${window.location.search}`,
              count: social?.count || 0,
            })
          })
          const localAdBalance =
            campaign?.localAdBalances?.reduce((acc: number, lab) => {
              return acc + lab?.local_ads_balance
            }, 0) || 0
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          return {
            ...campaign,
            activateUrl,
            cardImage: cloudinary
              .instance()
              .url(campaign?.logo_public_id || '', {
                background: 'auto:predominant',
                crop: 'pad',
                width: 500 * 2,
                height: 180 * 2,
                fetch_format: 'jpg',
                quality: 'auto',
              }),
            thumbnailSrc: campaign?.logo_url,
            startDate: campaign?.start_date,
            endDate: campaign?.end_date,
            title: campaign?.name,
            description: campaign?.description,
            brandLogo: campaign?.brand?.logo_url,
            type: localAdBalance ? 'localAd' : 'campaign',
            isActivated: campaign?.invitation?.state === 'engaged',
            channels,
            creditAmount: localAdBalance,
            sash:
              ((campaign?.invitation?.state === 'engaged' &&
                campaign?.invitation?.status !== 'accepted') ||
                campaign?.invitation?.state !== 'engaged') &&
              new Date(campaign?.invitation?.created_at || '') >= sevenDaysAgo,
            onViewOrActivate: (_: React.MouseEvent, channelUrl: Location) => {
              channelUrl
                ? (window.location = channelUrl)
                : (window.location.href = `${activateUrl}${window.location.search}`)
            },
          }
        }) || []
      setCampaignCards((prev) => {
        return [...prev, ...results]
      })
    }
  }, [isLoading, data, retailerAccount])
  const clearAndReset = () => {
    resetFilter()
    setCampaignCards([])
  }
  useOnRetailerAccountChange(clearAndReset)

  return filterBarRef ? (
    <>
      {createPortal(
        <ContentFilters
          brandFilter={brandFilter}
          filterInfo={filterInfo}
          onClear={clearAndReset}
          onChange={(key, value) => {
            updateFilter({ [key]: value })
            setCampaignCards([])
          }}
        />,
        filterBarRef,
        'organic-campaigns-filter-bar',
      )}

      <CampaignResultsContainer
        pageSize={pageSize}
        isLoading={isLoading}
        results={campaignCards}
        showMoreHandler={() => {
          setPage(filterInfo.page + 1)
        }}
        resetFilters={clearAndReset}
        totalResults={filterInfo.totalResults}
        noResultsText={I18n.t('content.noCampaignsAtAll')}
      >
        {!isLoading &&
          (filterInfo.totalResults === campaignCards.length ||
            campaignCards.length === 0) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                color="secondary"
                onClick={toggleModal}
                size="large"
                variant="outlined"
              >
                {I18n.t('content.requestMore')}
              </Button>
            </Box>
          )}
      </CampaignResultsContainer>

      <RequestCampaignsDialog
        onClose={toggleModal}
        open={isOpen}
        total={filterInfo.totalResults}
      />
    </>
  ) : null
}

export default OrganicCampaigns
